// Feedback helpers: mapping /api/feedbacks rows onto the design's vocabulary.
// Re-exported by supportKit.js, so existing imports keep working.

const toMs = (v) => (v ? new Date(v).getTime() : null);
const clean = (s) => String(s || "").trim();
const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

/**
 * feedbacks.status → the design's status words. "replied" is set by
 * POST /:id/reply (item 213) and "escalated" when the feedback became a
 * complaint (item 214); the other three are set by hand.
 */
export const FEEDBACK_STATUS_LABEL = { pending: "New", reviewed: "Reviewed", acknowledged: "Acknowledged", replied: "Replied", escalated: "Escalated to complaint" };
/** Every status, for filters. */
export const FEEDBACK_STATUSES = Object.values(FEEDBACK_STATUS_LABEL);
/** The statuses a person picks in the drawer; replied and escalated come from those actions. */
export const FEEDBACK_MANUAL_STATUSES = ["New", "Reviewed", "Acknowledged"];
export const feedbackStatusKey = (label) => Object.keys(FEEDBACK_STATUS_LABEL).find((k) => FEEDBACK_STATUS_LABEL[k] === label) || "pending";

/** Where the feedback came from (item 211): the backend's FEEDBACK_SOURCES. */
export const FEEDBACK_SOURCE_LABEL = { whatsapp: "WhatsApp", phone: "Phone call", in_clinic: "In clinic", email: "Email", google_review: "Google review", ai_front_desk: "AI front desk", other: "Other" };

/** Experience ratings (item 212): backend key → the design's label. */
export const ASPECT_LABEL = { doctor: "Doctor", staff: "Staff", wait_time: "Wait time", cleanliness: "Cleanliness", value: "Value" };

/** Sentiment from the star rating, as the old feedback page worked it out. */
export const sentimentOf = (rating) => (rating >= 4 ? "Positive" : rating >= 3 ? "Neutral" : "Negative");

/**
 * NPS over a list of 0–10 answers (item 210): % fans (9–10) minus % unhappy
 * (0–6), -100 to +100. Null when nobody answered — the same rule as the
 * backend's /api/feedbacks/stats.
 */
export function npsOf(scores) {
  const answered = scores.filter((s) => s !== null && s !== undefined);
  if (!answered.length) return { score: null, responses: 0, promoters: 0, passives: 0, detractors: 0 };
  const promoters = answered.filter((s) => s >= 9).length;
  const detractors = answered.filter((s) => s <= 6).length;
  return { score: Math.round(((promoters - detractors) * 100) / answered.length), responses: answered.length, promoters, passives: answered.length - promoters - detractors, detractors };
}

/** GET /api/feedbacks (or /:id) row → the design's Feedback. */
export function toFeedback(row) {
  const anonymous = !!Number(row.is_anonymous);
  const rating = Number(row.rating) || 0;
  const aspects = row.aspects && typeof row.aspects === "object" ? row.aspects : null;
  return {
    id: row.id,
    code: row.feedback_number || `#${row.id}`,
    contactId: anonymous ? null : row.contact_id || null,
    patientId: anonymous ? null : row.patient_id || null,
    anonymous,
    facilityId: row.facility_id || null,
    location: row.facility_name || "",
    name: anonymous ? "" : clean(row.contact_name) || clean(row.patient_name),
    phone: anonymous ? "" : row.contact_phone || row.patient_phone || "",
    rating,
    comment: row.feedback_text || "",
    sentiment: sentimentOf(rating),
    status: row.status || "pending",
    statusLabel: FEEDBACK_STATUS_LABEL[row.status] || "New",
    categoryId: row.category_id || null,
    category: row.category_name || "",
    adminNotes: row.admin_notes || "",
    createdAt: toMs(row.created_at) || 0,
    // Items 210–212: null when not given.
    nps: num(row.nps),
    doctorId: row.doctor_id || null,
    doctorName: clean(row.doctor_name),
    source: row.source || null,
    sourceLabel: row.source ? FEEDBACK_SOURCE_LABEL[row.source] || row.source : "",
    appointmentId: row.appointment_id || null,
    visitAt: toMs(row.visit_at),
    aspects: aspects ? Object.fromEntries(Object.keys(ASPECT_LABEL).map((k) => [k, num(aspects[k])])) : null,
    // Item 213: the reply and whether WhatsApp took it ("sent" / "failed", null when only saved).
    reply: row.reply_text || "",
    repliedAt: toMs(row.replied_at),
    repliedBy: clean(row.replied_by_name),
    replyWhatsapp: row.reply_whatsapp_status || null,
    // Item 214: the complaint this feedback was escalated to.
    complaintId: row.complaint_id || null,
    complaintCode: row.complaint_ticket_number || (row.complaint_id ? `#${row.complaint_id}` : ""),
  };
}
